import { useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  BackIcon,
  EmailField,
  FeedbackIcon,
  FormRow,
  Heading,
  Button,
  Text,
  TextLink,
  useDebouncedValue,
  useFormField,
  useAlert,
} from '@frontend/design-system';
import { sendPasswordReset } from '../services';

interface Props {
  initialUsername?: string;
  onBack?: (username?: string) => void;
}

export const PasswordResetOIDC = ({ initialUsername = '', onBack }: Props) => {
  const { t } = useTranslation('SignIn');
  const alerts = useAlert();
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // username props
  const usernameProps = useFormField({ type: 'email', required: true, value: initialUsername });
  const isUsernameComplete = useDebouncedValue(!usernameProps.error);
  const username = useDebouncedValue(usernameProps.value);

  const handleResetPassword = async () => {
    try {
      setIsResettingPassword(true);
      await sendPasswordReset(username);
      setPasswordResetSuccess(true);
    } catch {
      alerts.error(t('Failed to send the password reset link'));
    } finally {
      setIsResettingPassword(false);
    }
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        margin: 0 15%;
        min-width: 300px;
        max-width: 400px;
        align-items: ${passwordResetSuccess ? 'center' : 'initial'};
        text-align: ${passwordResetSuccess ? 'center' : 'initial'};
      `}
    >
      <FeedbackIcon
        css={css`
          display: ${passwordResetSuccess ? 'block' : 'none'};
          color: ${theme.colors.primary50};
          align-items: center;
        `}
        size={40}
      />
      <Heading
        css={css`
          margin: ${theme.spacing(2, 0)};
        `}
      >
        {!passwordResetSuccess ? t('Forgot password?') : t('Success')}
      </Heading>
      <Text>
        {!passwordResetSuccess
          ? t("No worries, we'll send you reset instructions")
          : t('If your user credentials exist in our system, we have sent you a password reset link via email.')}
      </Text>
      <form
        css={css`
          display: ${!passwordResetSuccess ? 'flex' : 'none'};
          flex-direction: column;
        `}
        id='password-reset-form'
        data-testid='password-reset-form'
      >
        <FormRow
          css={css`
            text-align: initial;
            margin: ${theme.spacing(4, 0)};
          `}
        >
          <EmailField name='username' {...usernameProps} label={t('Email')} />
        </FormRow>

        <Button
          variant='primary'
          onClick={handleResetPassword}
          disabled={!isUsernameComplete || isResettingPassword}
          type='button'
          size='large'
          id='reset-password-button'
        >
          {t('Reset Password')}
        </Button>
      </form>

      <TextLink
        css={css`
          display: flex;
          margin: ${theme.spacing(4, 0, 0)};
          cursor: pointer;
          text-align: left;
          width: 100%;
        `}
        onClick={() => onBack?.(usernameProps.value)}
        id='back-to-login-button'
      >
        <>
          <BackIcon
            css={css`
              margin-right: ${theme.spacing(1)};
            `}
          />
          {t('Back to login')}
        </>
      </TextLink>
    </div>
  );
};
